<template>
  <KeywordListFloatingBtnMobile
    v-bind="$attrs"
    v-model="open"
    :list="defaultSelectedList"
  />
  <DrawerRoot v-model:open="open">
    <DrawerPortal>
      <DrawerOverlay class="fixed inset-0 bg-black/50" />
      <DrawerContent
        class="fixed inset-x-0 bottom-0 z-[95]
        flex h-full max-h-[75%] flex-col rounded-t-[12px]
        bg-[--v3-root-surface-card] pt-[28px] outline-none"
      >
        <div class="bottom-menu relative size-full">
          <div
            class="transform-all relative size-full duration-150"
            :class="{
              '-translate-x-full': !!selectedList || snapshot.matches('CREATING')
            }"
          >
            <div class="absolute left-0 top-0 size-full translate-x-full bg-white">
              <KeywordListSliderPanel
                v-if="selectedList && snapshot.matches('LISTING')"
                class="h-[calc(100%-64px)] w-full"
                :list-id="selectedList.id"
                :list-name="selectedList?.name"
                :keywords="selectedList?.keywords"
                :color="selectedList?.color"
                :is-attempting-delete
                :is-deleting
                :can-return="true"
                :deleting-keyword="deletingKeyword"
                @attempt-delete-list="handleAttemptDeleteList"
                @attempt-delete-keyword="handleAttemptDeleteKeyword"
                @cancel="handleCancelDelete"
                @confirm="handleConfirm"
                @open-list-page="handleOpenListPage"
                @return="handleListReturn"
              />
              <Transition name="fade">
                <KeywordListNewListPanel
                  v-if="snapshot.matches('CREATING')"
                  :creating="isPendingKeywordListCreate"
                  @create="handleCreate"
                  @return="handleList"
                />
              </Transition>
            </div>
            <KeywordListPanel
              class="h-[calc(100%-64px)] w-full"
              :lists
              :loading="isLoading"
              :selected-list-id="selectedList?.id"
              :dragging-keyword="draggingKeyword"
              :lists-loading="listsLoading"
              @toggle="handleToggle"
              @create="handleCreateNewList"
              @on-add-keyword-to-list="handleOnAddKeywordToList"
            />
          </div>
          <div class="absolute bottom-0 left-0 h-[64px] w-full" @click="open = !open" />
        </div>
      </DrawerContent>
    </DrawerPortal>
  </DrawerRoot>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import {
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
} from 'vaul-vue';
import { computed, ref, watch } from 'vue';

import KeywordListFloatingBtnMobile from '@/common/components/molecules/KeywordListFloatingBtnMobile';
import KeywordListNewListPanel from '@/common/components/organisms/KeywordListNewListPanel';
import KeywordListPanel from '@/common/components/organisms/KeywordListPanel';
import KeywordListSliderPanel from '@/common/components/organisms/KeywordListSliderPanel';
import {
  useKeywordListCreateMutation,
} from '@/common/queries/keyword-list';
import { useKeywordListStore } from '@/common/stores/lists';
import { useSubscriptionStore } from '@/common/stores/subscription';
import type { keywordListMachineActor, keywordListMachineSnapshop } from '@/common/types';

interface Props {
  actor: keywordListMachineActor,
  isDeleting: boolean,
  isLoading: boolean,
  lastActiveListId: number,
  lists: {
    color: string,
    id: number,
    keywords: {
      flags?: string,
      id: number,
      keyword: string,
      listId: number,
      notes?: string,
      tags?: number,
    }[]
    name: string,
  }[],
  listsLoading: Set<number>,
  snapshot: keywordListMachineSnapshop,
  variant?: 'green' | 'orange',
}

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
  variant: 'green',
});

const {
  mutate: mutateKeywordListCreate,
  isPending: isPendingKeywordListCreate,
} = useKeywordListCreateMutation();

const open = ref<boolean>(false);
const { reloadQuotaNoneDaily } = useSubscriptionStore();

const keywordListStore = useKeywordListStore();
const {
  removeList,
  removeKeywordList,
  addTermsToList,
} = keywordListStore;
const { lastActiveListId: _lastActiveListId } = storeToRefs(keywordListStore);

const selectedListId = computed<number>(() => props.snapshot.context.selectedList);
const selectedList = computed(() => props.lists.find((item) => item.id === selectedListId.value));
const isAttemptingDelete = computed(() => !!(props.snapshot.context.deletingListId || props.snapshot.context.deletingKeywordId));
const deletingKeyword = computed<string | null | undefined>(() => {
  const deletingKeywordId = props.snapshot.context.deletingKeywordId;

  if (!deletingKeywordId) return null;
  const selectedKeyword = selectedList.value.keywords.find((item) => item.id ===  deletingKeywordId);
  return selectedKeyword?.keyword;
});

const draggingKeyword = computed(() =>  props.snapshot.context.draggingKeyword);
const defaultSelectedList = computed(() => {
  if (!props.lastActiveListId) return null;

  return props.lists.find((item) => item.id === props.lastActiveListId);
});

const handleToggle = (listId) => {
  props.actor.send({ type: 'select', listId });
  _lastActiveListId.value = listId;
};

const handleCreateNewList = () => {
  props.actor.send({ type: 'attempt_create' });
};

const handleCreate = (listName: string) => {
  mutateKeywordListCreate({
    name: listName,
  }, {
    onSuccess: () => {
      props.actor.send({ type: 'create' });
      reloadQuotaNoneDaily();
    },
  });
};

const handleList = () => {
  props.actor.send({ type: 'list' });
};

const handleListReturn = () => {
  props.actor.send({ type: 'reset' });
};

const handleCancelDelete = () => {
  props.actor.send({ type: 'cancel' });
};

const handleAttemptDeleteList = () => {
  props.actor.send({ type: 'attempt_delete_list', listId: selectedListId.value  });
};

const handleAttemptDeleteKeyword = (keywordId: number) => {
  props.actor.send({ type: 'attempt_delete_keyword', listId: selectedListId.value, keywordId  });
};

const handleConfirm = () => {
  if (props.snapshot.context.deletingKeywordId) {
    removeKeywordList(props.snapshot.context.deletingKeywordId).then(() => {
      props.actor.send({ type: 'confirm' });
    });
  } else {
    removeList(selectedListId.value).then(() => {
      reloadQuotaNoneDaily();
      props.actor.send({ type: 'confirm' });
    });
  }
};

const handleOnAddKeywordToList = (listId, keyword) => {
  addTermsToList(listId, keyword);
};

const handleOpenListPage = () => {
  open.value = false;
  props.actor.send({ type: 'reset' });
};


watch(open, (newVal) => {
  if (newVal) {
    props.actor.send({ type: 'reset' });
  }

  // @ts-expect-error - Intercom is loaded in the window.
  window.Intercom?.('update', {
    hide_default_launcher: newVal,
  });
});
</script>

<style scoped>
.fade-enter-from {
  opacity: 0;
}

.fade-enter-to {
  opacity: 1;
}

.fade-enter-active {
  transition: opacity 150ms;
}

.bottom-menu {
  position: relative;
}

.bottom-menu::after {
  content: '.';
  width: 25%;
  height: 4px;
  top: -15px;
  background-color: var(--v3-blue-gray-bluegray-200);
  border-radius: 6px;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
}
</style>
